import { useEffect, type ReactNode } from 'react';
import { useThemeStore } from '@app/theme/theme.store';

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const mode = useThemeStore((s) => s.mode);
  const style = useThemeStore((s) => s.style);
  const hasUserPreference = useThemeStore((s) => s.hasUserPreference);
  const setMode = useThemeStore((s) => s.setMode);

  // Apply theme to <html data-theme="…" data-style="…">
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', mode);
    root.setAttribute('data-style', style);
    const themeColor =
      style === 'neo-brutalist'
        ? mode === 'dark'
          ? '#1a1a17'
          : '#fafaf5'
        : style === 'greta'
          ? mode === 'dark'
            ? '#2a2825'
            : '#f6f5f2'
          : mode === 'dark'
            ? '#0e0e0c'
            : '#faf8f3';
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', themeColor);
  }, [mode, style]);

  // Follow system theme until the user explicitly toggles.
  useEffect(() => {
    if (hasUserPreference) return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e: MediaQueryListEvent) => {
      const next = e.matches ? 'light' : 'dark';
      // Direct set without flipping hasUserPreference.
      useThemeStore.setState({ mode: next });
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
    // setMode is stable; we just want to track hasUserPreference.
  }, [hasUserPreference, setMode]);

  return <>{children}</>;
}
